"use client";

import { createContext, useContext, useState, ReactNode, useCallback } from "react";

interface MobileNavContextType {
  title: string;
  setTitle: (title: string) => void;
  leftActions: ReactNode;
  setLeftActions: (actions: ReactNode) => void;
  rightActions: ReactNode;
  setRightActions: (actions: ReactNode) => void;
  // 用于返回按钮
  showBackButton: boolean;
  setShowBackButton: (show: boolean) => void;
  onBack: (() => void) | null;
  setOnBack: (handler: (() => void) | null) => void;
}

const MobileNavContext = createContext<MobileNavContextType | null>(null);

export function MobileNavProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState("");
  const [leftActions, setLeftActions] = useState<ReactNode>(null);
  const [rightActions, setRightActions] = useState<ReactNode>(null);
  const [showBackButton, setShowBackButton] = useState(false);
  const [onBack, setOnBack] = useState<(() => void) | null>(null);

  return (
    <MobileNavContext.Provider
      value={{
        title,
        setTitle,
        leftActions,
        setLeftActions,
        rightActions,
        setRightActions,
        showBackButton,
        setShowBackButton,
        onBack,
        setOnBack: useCallback((handler: (() => void) | null) => setOnBack(() => handler), []),
      }}
    >
      {children}
    </MobileNavContext.Provider>
  );
}

export function useMobileNav() {
  const context = useContext(MobileNavContext);
  if (!context) {
    throw new Error("useMobileNav must be used within MobileNavProvider");
  }
  return context;
}

/**
 * Hook 用于页面设置 MobileNav 内容
 * 在组件卸载时自动清理
 */
export function useMobileNavConfig(config: {
  title?: string;
  rightActions?: ReactNode;
  showBackButton?: boolean;
  onBack?: () => void;
}) {
  const { setTitle, setRightActions, setShowBackButton, setOnBack } = useMobileNav();

  // 使用 useEffect 在 mount 时设置，unmount 时清理
  // 但这里不能直接 import useEffect，所以返回一个设置函数
  return {
    apply: () => {
      if (config.title !== undefined) setTitle(config.title);
      if (config.rightActions !== undefined) setRightActions(config.rightActions);
      if (config.showBackButton !== undefined) setShowBackButton(config.showBackButton);
      if (config.onBack !== undefined) setOnBack(config.onBack);
    },
    clear: () => {
      setTitle("");
      setRightActions(null);
      setShowBackButton(false);
      setOnBack(null);
    },
  };
}
